import { ApiClient } from '../client'
import { 
  Campaign, 
  CreateCampaignRequest, 
  ApiResponse,
  SelectTargetsResponse,
  StartCampaignResponse 
} from '../types'

export class CampaignApi {
  constructor(private client: ApiClient) {}

  async list(orgId?: string): Promise<Campaign[]> {
    // Simplified for single org view
    const params: Record<string, string> = orgId ? { organisation_id: orgId } : {}
    const response = await this.client.get<any>('/v1/campaigns', params)
    
    // Handle both paginated and direct array responses
    if (Array.isArray(response)) {
      return response
    } else if (response?.data?.campaigns && Array.isArray(response.data.campaigns)) {
      // Handle nested campaigns array in data
      return response.data.campaigns
    } else if (response?.data && Array.isArray(response.data)) {
      return response.data
    } else if (response?.items && Array.isArray(response.items)) {
      return response.items
    } else if (response?.campaigns && Array.isArray(response.campaigns)) {
      // Handle campaigns at root level
      return response.campaigns
    }
    
    return []
  }

  async get(campaignId: string): Promise<Campaign> {
    const response = await this.client.get<any>(`/v1/campaigns/${campaignId}`)
    
    // Handle wrapped or unwrapped response
    if (response?.data) {
      return response.data as Campaign
    }
    return response as Campaign
  }

  async create(data: CreateCampaignRequest): Promise<ApiResponse<Campaign>> {
    return this.client.post<ApiResponse<Campaign>>('/v1/campaigns', data)
  }

  async update(campaignId: string, data: any): Promise<ApiResponse<Campaign>> {
    return this.client.put<ApiResponse<Campaign>>(`/v1/campaigns/${campaignId}`, data)
  }

  async delete(campaignId: string): Promise<void> {
    return this.client.delete<void>(`/v1/campaigns/${campaignId}`)
  }

  async selectTargets(
    campaignId: string, 
    data: { 
      method: string
      sample_size?: number
      specific_emails?: string[]
      exclude_emails?: string[] 
    }
  ): Promise<SelectTargetsResponse> {
    return this.client.post<SelectTargetsResponse>(`/v1/campaigns/${campaignId}/targets`, data)
  }

  async start(campaignId: string): Promise<StartCampaignResponse> {
    return this.client.post<StartCampaignResponse>(`/v1/campaigns/${campaignId}/start`, {})
  }

  async getResults(campaignId: string, includeDetails = false): Promise<ApiResponse<any>> {
    return this.client.get<ApiResponse<any>>(`/v1/campaigns/${campaignId}/results`, { includeDetails })
  }

  async schedule(campaignId: string, data: { start_date: string; end_date?: string }): Promise<ApiResponse<Campaign>> {
    return this.client.post<ApiResponse<Campaign>>(`/v1/campaigns/${campaignId}/schedule`, data)
  }

  async unschedule(campaignId: string): Promise<ApiResponse<Campaign>> {
    return this.client.delete<ApiResponse<Campaign>>(`/v1/campaigns/${campaignId}/schedule`)
  }
}
